import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { AppStore } from '../app-store/app-store.service';
import { ResponseApi } from 'src/app/models/branches.model';
import { ApiOperations } from 'src/app/models';
import { CacheScheduleStore } from 'src/app/models';

@Injectable({
  providedIn: 'root',
})
export class ScheduleService {
  private apiOperations: ApiOperations | undefined;
  public cacheData: CacheScheduleStore = {
    regions: [],
    series: [],
    days: [],
    hours: [],
  };

  constructor(private http: HttpClient, private appStore: AppStore) {
    const { operations } = this.appStore.env?.api ?? {};
    this.apiOperations = operations;
  }

  handleError(error: any): Observable<any> {
    console.error(error);
    return throwError(() => error);
  }

  getOffices(): Observable<ResponseApi> {
    if (!this.apiOperations) {
      return throwError('this.apiOperations is not set');
    }
    const url = this.appStore.getUrlByOperation(this.apiOperations.getOffices);
    return this.http.get<ResponseApi>(url).pipe(
      tap((response) => {
        this.cacheData.regions = response?.regiones ?? [];
      }),
      tap((result) => result),
      catchError(this.handleError)
    );
  }

  getSeries({ idOficina }: { idOficina: string }): Observable<any> {
    if (!this.apiOperations) {
      return throwError('this.apiOperations is not set');
    }
    const queryParams = new HttpParams().append('idOficina', idOficina);
    const url = this.appStore.getUrlByOperation(this.apiOperations.getSeries);
    return this.http.get<ResponseApi>(url, { params: queryParams }).pipe(
      tap((response) => {
        const { listadoseries: series } = response;
        this.cacheData.series = series ?? [];
      }),
      tap((result: any) => result),
      catchError(this.handleError)
    );
  }

  getDays({
    idOficina,
    idSerie,
  }: {
    idOficina: string;
    idSerie: string;
  }): Observable<any> {
    if (!this.apiOperations) {
      return throwError(() => new Error('this.this.apiOperations is not set'));
    }
    const queryParams = new HttpParams()
      .append('idOficina', idOficina)
      .append('idSerie', idSerie);
    const url = this.appStore.getUrlByOperation(this.apiOperations.getDays);
    return this.http.get<any>(url, { params: queryParams }).pipe(
      tap((response) => {
        const { listardias: days } = response;
        this.cacheData.days = days ?? [];
      }),
      tap((result) => result)
    );
  }

  getHours({
    idOficina,
    idSerie,
    dia,
  }: {
    idOficina: string;
    idSerie: string;
    dia: string;
  }): Observable<any> {
    if (!this.apiOperations) {
      return throwError(() => new Error('this.this.apiOperations is not set'));
    }
    const queryParams = new HttpParams()
      .append('idOficina', idOficina)
      .append('idSerie', idSerie)
      .append('dia', dia);
    const url = this.appStore.getUrlByOperation(this.apiOperations.getHours);
    return this.http.get<any>(url, { params: queryParams }).pipe(
      tap((response) => {
        const { listarHoras: hours } = response;
        this.cacheData.hours = hours ?? [];
      }),
      tap((result) => result),
      catchError(this.handleError)
    );
  }

  getSchedules({ rut }: { rut: string }): Observable<any> {
    if (!this.apiOperations) {
      return throwError(() => new Error('this.this.apiOperations is not set'));
    }
    const queryParams = new HttpParams().append('rut', rut);
    const url = this.appStore.getUrlByOperation(
      this.apiOperations.getSchedules
    );
    return this.http.get<Observable<any>>(url, { params: queryParams }).pipe(
      tap((result) => result),
      catchError(this.handleError)
    );
  }

  getSchedulesBy(
    body: {
      reservaRequest: {
        rut: string;
        dia: string | null;
      };
      recaptchaRequest: {
        nameVaultSecret: string;
        recaptchaToken: string;
      };
    } | null
  ): Observable<any> {
    if (!this.apiOperations) {
      return throwError('this.this.apiOperations is not set');
    }
    const url = this.appStore.getUrlByOperation(
      this.apiOperations.getSchedulesBy
    );
    return this.http.post<Observable<any>>(url, body).pipe(
      tap((result) => result),
      catchError(this.handleError)
    );
  }

  setSchedule(
    body: {
      agendaRequest: {
        idOficina: number;
        idSerie: number;
        dia: string | null;
        hora: string;
        rut: string;
        mail: string;
        telefono: string;
      };
      recaptchaRequest: {
        nameVaultSecret: string;
        recaptchaToken: string;
      };
    } | null
  ): Observable<any> {
    if (!this.apiOperations) {
      return throwError('this.this.apiOperations is not set');
    }
    const url = this.appStore.getUrlByOperation(this.apiOperations.setSchedule);
    return this.http.post<any>(url, body).pipe(tap((result) => result));
  }

  getValidationRut(
    body: {
      reservaRequest: {
        rut: string;
        origen: number;
      };
      recaptchaRequest: {
        nameVaultSecret: string;
        recaptchaToken: string;
      };
    } | null
  ): Observable<any> {
    if (!this.apiOperations) {
      return throwError('this.this.apiOperations is not set');
    }
    const url = this.appStore.getUrlByOperation(
      this.apiOperations.validationRut
    );

    return this.http.post<Observable<any>>(url, body).pipe(
      tap((result) => result),
      catchError(this.handleError)
    );
  }
}
